import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../utils';
import {
  getTreatmentPlanV3FamilyAdditionalInfoArray,
  setTreatmentPlanV3FamilyAdditionalInfoArray,
} from './utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type {
  IClientSibling,
  IFamily,
  IFamilyMember,
  IFamilyValueItem,
} from '../../../../pages/common';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  CreateTreatmentPlanV3ClientSiblingPayload,
  CreateTreatmentPlanV3FamilyAdditionalInfoPayload,
  CreateTreatmentPlanV3FamilyMemberPayload,
  DeleteTreatmentPlanV3ClientSiblingPayload,
  DeleteTreatmentPlanV3FamilyAdditionalInfoPayload,
  DeleteTreatmentPlanV3FamilyMemberPayload,
  FamilyMutationAction,
  TreatmentPlanV3FamilyThunks,
  UpdateTreatmentPlanV3ClientSiblingPayload,
  UpdateTreatmentPlanV3FamilyMemberPayload,
  UpdateTreatmentPlanV3FamilyPayload,
} from './types';

export function addTreatmentPlanV3FamilyCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  addGetTreatmentPlanV3FamilyPageCases(builder, thunks);
  addUpdateTreatmentPlanV3FamilyCases(builder, thunks);
  addCreateTreatmentPlanV3FamilyMemberCases(builder, thunks);
  addUpdateTreatmentPlanV3FamilyMemberCases(builder, thunks);
  addDeleteTreatmentPlanV3FamilyMemberCases(builder, thunks);
  addCreateTreatmentPlanV3ClientSiblingCases(builder, thunks);
  addUpdateTreatmentPlanV3ClientSiblingCases(builder, thunks);
  addDeleteTreatmentPlanV3ClientSiblingCases(builder, thunks);
  addCreateTreatmentPlanV3FamilyAdditionalInfoCases(builder, thunks);
  addDeleteTreatmentPlanV3FamilyAdditionalInfoCases(builder, thunks);
}

function isForLoadedFamilyPage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.familyPageTreatmentPlanId;
}

function markFamilyPageChanged(state: TreatmentPlanV3State) {
  if (state.familyPageLoading) {
    state.familyPageChangedSinceRequest = true;
  }
}

function addGetTreatmentPlanV3FamilyPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { getTreatmentPlanV3FamilyPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3FamilyPage.pending, (state, action) => {
      // A reload of the same plan keeps the current page so saves that land while it is in
      // flight still apply — only switching plans discards it.
      if (!isForLoadedFamilyPage(state, action)) {
        state.familyPage = null;
      }
      state.familyPageLoading = true;
      state.familyPageError = null;
      state.familyPageRequestId = action.meta.requestId;
      state.familyPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.familyPageChangedSinceRequest = false;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3FamilyPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.familyPageRequestId) return;
      state.familyPageLoading = false;
      state.familyPageError = null;
      // The response was built before any save that finished while it was in flight, so
      // adopting it would roll that save back — keep the mutated copy instead.
      if (!state.familyPageChangedSinceRequest || !state.familyPage) {
        state.familyPage = action.payload;
      }
      state.familyPageChangedSinceRequest = false;
    })
    .addCase(getTreatmentPlanV3FamilyPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.familyPageRequestId) return;
      state.familyPageLoading = false;
      state.familyPageError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateTreatmentPlanV3FamilyCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { updateTreatmentPlanV3Family } = thunks;
  const flag = 'updateFamilyLoading';

  builder
    .addCase(updateTreatmentPlanV3Family.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3Family.fulfilled,
      (state, action: FamilyMutationAction<IFamily, UpdateTreatmentPlanV3FamilyPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action) || !state.familyPage) return;
        markFamilyPageChanged(state);
        // The scalar PATCH response embeds the member/sibling/value-item lists as of when the
        // backend handled it, so adopting them wholesale rolls back list changes saved while it
        // was in flight. Those lists are maintained by their own reducers — keep our copies.
        state.familyPage = {
          ...action.payload,
          family_members: state.familyPage.family_members,
          client_siblings: state.familyPage.client_siblings,
          maternal_medical_history: state.familyPage.maternal_medical_history,
          paternal_medical_history: state.familyPage.paternal_medical_history,
          community_support: state.familyPage.community_support,
        };
      },
    )
    .addCase(updateTreatmentPlanV3Family.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addCreateTreatmentPlanV3FamilyMemberCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { createTreatmentPlanV3FamilyMember } = thunks;
  const flag = 'createFamilyMemberLoading';

  builder
    .addCase(createTreatmentPlanV3FamilyMember.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3FamilyMember.fulfilled,
      (
        state,
        action: FamilyMutationAction<IFamilyMember, CreateTreatmentPlanV3FamilyMemberPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action) || !state.familyPage) return;
        markFamilyPageChanged(state);
        state.familyPage.family_members.unshift(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3FamilyMember.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addUpdateTreatmentPlanV3FamilyMemberCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { updateTreatmentPlanV3FamilyMember } = thunks;
  const flag = 'updateFamilyMemberLoading';

  builder
    .addCase(updateTreatmentPlanV3FamilyMember.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3FamilyMember.fulfilled,
      (
        state,
        action: FamilyMutationAction<IFamilyMember, UpdateTreatmentPlanV3FamilyMemberPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action)) return;
        const list = state.familyPage?.family_members;
        if (!list) return;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index === -1) return;
        markFamilyPageChanged(state);
        list[index] = action.payload;
      },
    )
    .addCase(updateTreatmentPlanV3FamilyMember.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3FamilyMemberCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { deleteTreatmentPlanV3FamilyMember } = thunks;
  const flag = 'deleteFamilyMemberLoading';

  builder
    .addCase(deleteTreatmentPlanV3FamilyMember.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3FamilyMember.fulfilled,
      (state, action: FamilyMutationAction<void, DeleteTreatmentPlanV3FamilyMemberPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action) || !state.familyPage) return;
        markFamilyPageChanged(state);
        state.familyPage.family_members = state.familyPage.family_members.filter(
          (row) => row.id !== action.meta.arg.memberId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3FamilyMember.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addCreateTreatmentPlanV3ClientSiblingCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { createTreatmentPlanV3ClientSibling } = thunks;
  const flag = 'createClientSiblingLoading';

  builder
    .addCase(createTreatmentPlanV3ClientSibling.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3ClientSibling.fulfilled,
      (
        state,
        action: FamilyMutationAction<IClientSibling, CreateTreatmentPlanV3ClientSiblingPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action) || !state.familyPage) return;
        markFamilyPageChanged(state);
        state.familyPage.client_siblings.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3ClientSibling.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addUpdateTreatmentPlanV3ClientSiblingCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { updateTreatmentPlanV3ClientSibling } = thunks;
  const flag = 'updateClientSiblingLoading';

  builder
    .addCase(updateTreatmentPlanV3ClientSibling.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3ClientSibling.fulfilled,
      (
        state,
        action: FamilyMutationAction<IClientSibling, UpdateTreatmentPlanV3ClientSiblingPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action)) return;
        const list = state.familyPage?.client_siblings;
        if (!list) return;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index === -1) return;
        markFamilyPageChanged(state);
        list[index] = action.payload;
      },
    )
    .addCase(updateTreatmentPlanV3ClientSibling.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3ClientSiblingCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { deleteTreatmentPlanV3ClientSibling } = thunks;
  const flag = 'deleteClientSiblingLoading';

  builder
    .addCase(deleteTreatmentPlanV3ClientSibling.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3ClientSibling.fulfilled,
      (state, action: FamilyMutationAction<void, DeleteTreatmentPlanV3ClientSiblingPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action) || !state.familyPage) return;
        markFamilyPageChanged(state);
        state.familyPage.client_siblings = state.familyPage.client_siblings.filter(
          (row) => row.id !== action.meta.arg.siblingId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3ClientSibling.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addCreateTreatmentPlanV3FamilyAdditionalInfoCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { createTreatmentPlanV3FamilyAdditionalInfo } = thunks;
  const flag = 'createFamilyAdditionalInfoLoading';

  builder
    .addCase(createTreatmentPlanV3FamilyAdditionalInfo.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3FamilyAdditionalInfo.fulfilled,
      (
        state,
        action: FamilyMutationAction<
          IFamilyValueItem,
          CreateTreatmentPlanV3FamilyAdditionalInfoPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action) || !state.familyPage) return;
        markFamilyPageChanged(state);
        getTreatmentPlanV3FamilyAdditionalInfoArray(
          state.familyPage,
          action.meta.arg.data.info_type,
        ).push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3FamilyAdditionalInfo.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3FamilyAdditionalInfoCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3FamilyThunks,
) {
  const { deleteTreatmentPlanV3FamilyAdditionalInfo } = thunks;
  const flag = 'deleteFamilyAdditionalInfoLoading';

  builder
    .addCase(deleteTreatmentPlanV3FamilyAdditionalInfo.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3FamilyAdditionalInfo.fulfilled,
      (
        state,
        action: FamilyMutationAction<void, DeleteTreatmentPlanV3FamilyAdditionalInfoPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedFamilyPage(state, action) || !state.familyPage) return;
        markFamilyPageChanged(state);
        const { infoType, itemId } = action.meta.arg;
        setTreatmentPlanV3FamilyAdditionalInfoArray(
          state.familyPage,
          infoType,
          getTreatmentPlanV3FamilyAdditionalInfoArray(state.familyPage, infoType).filter(
            (row) => row.id !== itemId,
          ),
        );
      },
    )
    .addCase(deleteTreatmentPlanV3FamilyAdditionalInfo.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedFamilyPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}
